import React, { useState, useEffect, FormEvent } from 'react';
import Button from '../common/Button';
import Input from '../common/Input';
import Toast from '../common/Toast';
import { apiService } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import './RegisterModal.css'; // Reuse modal styles

interface ChangePasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FormData {
  username: string;
  current_password: string;
  new_password: string;
  confirm_password: string;
}

interface FormErrors {
  username?: string;
  current_password?: string;
  new_password?: string;
  confirm_password?: string;
  general?: string;
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { logout, user } = useAuth();
  const [formData, setFormData] = useState<FormData>({
    username: user?.username || '',
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showToast, setShowToast] = useState(false);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setFormData({
        username: user?.username || '',
        current_password: '',
        new_password: '',
        confirm_password: '',
      });
      setErrors({});
      setSuccessMessage('');
      setShowToast(false);
    }
  }, [isOpen, user?.username]);

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined,
      }));
    }
    if (errors.general) {
      setErrors(prev => ({
        ...prev,
        general: undefined,
      }));
    }
    if (successMessage) {
      setSuccessMessage('');
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.username.trim()) {
      newErrors.username = 'Field Required';
    }

    if (!formData.current_password.trim()) {
      newErrors.current_password = 'Field Required';
    }

    if (!formData.new_password.trim()) {
      newErrors.new_password = 'Field Required';
    } else if (formData.new_password.length < 4) {
      newErrors.new_password = 'At least 4 characters';
    }

    if (!formData.confirm_password.trim()) {
      newErrors.confirm_password = 'Confirm your new password';
    } else if (formData.new_password !== formData.confirm_password) {
      newErrors.confirm_password = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsLoading(true);

    try {
      const response = await apiService.post('/auth/change_password', {
        username: formData.username,
        current_password: formData.current_password,
        new_password: formData.new_password,
        confirm_password: formData.confirm_password,
      });

      setSuccessMessage('Password changed successfully. Logging out...');
      setShowToast(true);

      setTimeout(() => {
        setShowToast(false);
      }, 3000);

      setTimeout(() => {
        logout();
        onClose();
      }, 1500);
    } catch (error: any) {
      let message = 'Password change failed. Please try again.';
      if (error.message) {
        message = error.message;
      } else if (error.detail) {
        message = error.detail;
      } else if (typeof error === 'string') {
        message = error;
      }
      setErrors(prev => ({
        ...prev,
        general: message,
      }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <>
      {isOpen && (
        <div className="register-modal-backdrop" onClick={handleBackdropClick}>
          <div className="register-modal" role="dialog" aria-labelledby="change-password-modal-title" aria-modal="true">
            <div className="register-modal__header">
              <h2 id="change-password-modal-title" className="register-modal__title">Change Password</h2>
              <button
                type="button"
                className="register-modal__close"
                onClick={onClose}
                aria-label="Close change password modal"
                disabled={isLoading}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <form className="register-modal__form" onSubmit={handleSubmit} noValidate>
              {successMessage && (
                <div className="register-modal__success" role="alert">
                  <svg className="register-modal__success-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 12l2 2 4-4" />
                    <circle cx="12" cy="12" r="10" />
                  </svg>
                  {successMessage}
                </div>
              )}
              {errors.general && (
                <div className="register-modal__error" role="alert">
                  <svg className="register-modal__error-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="15" y1="9" x2="9" y2="15" />
                    <line x1="9" y1="9" x2="15" y2="15" />
                  </svg>
                  {typeof errors.general === 'string' ? errors.general : JSON.stringify(errors.general)}
                </div>
              )}
              <div className="register-modal__fields">
                <div className="register-modal__field">
                  <Input
                    type="text"
                    name="username"
                    label="Username"
                    placeholder="Username"
                    value={formData.username}
                    onChange={handleInputChange}
                    error={errors.username}
                    disabled={isLoading || !!user?.username}
                    autoComplete="username"
                    required
                  />
                </div>
                <div className="register-modal__field">
                  <Input
                    type="password"
                    name="current_password"
                    label="Current Password"
                    placeholder="Current Password"
                    value={formData.current_password}
                    onChange={handleInputChange}
                    error={errors.current_password}
                    disabled={isLoading}
                    autoComplete="current-password"
                    required
                  />
                </div>
                <div className="register-modal__field">
                  <Input
                    type="password"
                    name="new_password"
                    label="New Password"
                    placeholder="New Password"
                    value={formData.new_password}
                    onChange={handleInputChange}
                    error={errors.new_password}
                    disabled={isLoading}
                    autoComplete="new-password"
                    required
                  />
                </div>
                <div className="register-modal__field">
                  <Input
                    type="password"
                    name="confirm_password"
                    label="Confirm New Password"
                    placeholder="Confirm New Password"
                    value={formData.confirm_password}
                    onChange={handleInputChange}
                    error={errors.confirm_password}
                    disabled={isLoading}
                    autoComplete="new-password"
                    required
                  />
                </div>
              </div>
              <div className="register-modal__actions">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={onClose}
                  disabled={isLoading}
                  className="register-modal__cancel-button"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  loading={isLoading}
                  disabled={isLoading || !!successMessage}
                  className="register-modal__submit-button"
                >
                  {isLoading ? 'Changing...' : 'Change Password'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
      {showToast && (
        <Toast
          message={successMessage}
          duration={3000}
          onClose={() => setShowToast(false)}
        />
      )}
    </>
  );
};

export default ChangePasswordModal;
